import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Sparkles, Bot, User, Loader2 } from "lucide-react";
import { CURRENT_TIERS } from "../data/aiStack";
import { streamChat, type ChatMessage } from "../lib/llmClient";

interface Msg { id: string; role: "user" | "assistant"; content: string; model?: string; }

const STARTERS = [
  "What's the status of n8n right now?",
  "Summarize my Cloudflare workers.",
  "Which Ollama models are oversized for the 4080?",
  "Explain ai-router in two sentences.",
];

export default function Chat() {
  const [model, setModel] = useState(CURRENT_TIERS[2].models[0]);
  const [msgs, setMsgs] = useState<Msg[]>([
    { id: "0", role: "assistant", content: "Wired to your LiteLLM proxy. Ask anything about your infra. Switch models above.", model: "system" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, loading]);

  async function send(text: string) {
    if (!text.trim() || loading) return;
    const userId = Date.now().toString();
    const assistantId = userId + "a";

    // Build the full conversation to send. Drop the welcome system bubble.
    const history: ChatMessage[] = msgs
      .filter((m) => m.model !== "system")
      .map((m) => ({ role: m.role, content: m.content }));
    history.push({ role: "user", content: text });

    setMsgs((m) => [
      ...m,
      { id: userId, role: "user", content: text },
      { id: assistantId, role: "assistant", content: "", model },
    ]);
    setInput("");
    setLoading(true);

    await streamChat(
      history,
      model,
      (delta) => {
        setMsgs((m) => m.map((msg) =>
          msg.id === assistantId ? { ...msg, content: msg.content + delta } : msg,
        ));
      },
      () => {
        setLoading(false);
      },
      (err) => {
        const code = err.status ? `HTTP ${err.status}` : "network";
        setMsgs((m) => m.map((msg) =>
          msg.id === assistantId
            ? { ...msg, content: `Request failed (${code}): ${err.message || "no detail"}` }
            : msg,
        ));
        setLoading(false);
      },
    );
  }

  const allModels = Array.from(new Set(CURRENT_TIERS.flatMap((t) => t.models)));

  return (
    <div className="h-[calc(100vh-9rem)] flex flex-col gap-4">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Layer · Tools</div>
          <h1 className="text-3xl font-bold mt-1 tracking-tight">Chat</h1>
          <p className="text-slate-400 mt-1 text-sm">Streaming via admin-llm-proxy to LiteLLM.</p>
        </div>
        <div className="glass px-3 py-2 flex items-center gap-2">
          <Sparkles size={14} className="text-accent-violet" />
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="bg-transparent text-sm text-slate-200 outline-none border-0"
          >
            {allModels.map((m) => <option key={m} className="bg-bg-900">{m}</option>)}
          </select>
          {loading && (
            <span className="ml-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              live
            </span>
          )}
        </div>
      </header>

      <div className="glass flex-1 flex flex-col overflow-hidden ring-glass">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
          <AnimatePresence initial={false}>
            {msgs.map((m) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}
              >
                {m.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-violet to-accent-blue flex items-center justify-center shrink-0">
                    <Bot size={15} />
                  </div>
                )}
                <div className={`max-w-[78%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-accent-blue/15 border border-accent-blue/25 text-slate-100"
                    : "bg-white/[0.03] border border-white/[0.06]"
                }`}>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                  {m.model && m.model !== "system" && <div className="eyebrow mt-2">{m.model}</div>}
                </div>
                {m.role === "user" && (
                  <div className="w-8 h-8 rounded-full bg-white/[0.06] flex items-center justify-center shrink-0">
                    <User size={15} />
                  </div>
                )}
              </motion.div>
            ))}
            {loading && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-violet to-accent-blue flex items-center justify-center">
                  <Bot size={15} />
                </div>
                <div className="px-4 py-3 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center gap-2 text-sm text-slate-400">
                  <Loader2 size={14} className="animate-spin" /> thinking…
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="border-t border-white/[0.04] p-4">
          <div className="flex gap-2 mb-3 flex-wrap">
            {STARTERS.map((s) => (
              <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.05] text-slate-400 transition">
                {s}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); send(input); }}
            className="flex items-center gap-2 glass px-3 py-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything…"
              className="flex-1 bg-transparent border-0 outline-0 text-sm placeholder:text-slate-600"
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="px-3 py-1.5 rounded-lg bg-gradient-to-br from-accent-blue to-accent-violet text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-[0_0_20px_rgba(96,165,250,0.25)]"
            >
              <Send size={13} /> Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
